import React, { useState, useRef } from 'react';
import { AutoComplete } from 'primereact/autocomplete';

const INEGI_API_KEY = process.env.REACT_APP_INEGI_KEY || '';

/**
 * Componente input con búsqueda a INEGI SAKBE y dropdown de resultados
 * Busca destinos en la Red Nacional de Caminos (localidades, sitios de interés, etc.)
 * @param {{
 *  placeholder: string,
 *  value?: string,
 *  onLocationSelect: (location: {lat:number, lng:number, name:string, address:string, id_dest:string}) => void
 * }} props
 */
const INEGIGeocoderInput = ({ placeholder, value = '', onLocationSelect, clearOnSelect = true }) => {
    const [query, setQuery] = useState(value);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounceTimeout = useRef(null);

    const searchINEGI = (event) => {
        const q = event.query;

        if (!q || q.length < 3) {
            setSuggestions([]);
            return;
        }

        // 🔥 limpiamos debounce previo
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(async () => {
            setLoading(true);
            try {
                const response = await fetch(
                    'https://gaia.inegi.org.mx/sakbe_v3.1/buscadestino',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        body: new URLSearchParams({
                            buscar: q,
                            type: 'json',
                            num: '10',
                            key: INEGI_API_KEY
                        })
                    }
                );

                if (!response.ok) {
                    throw new Error(`Error en la búsqueda INEGI: ${response.status}`);
                }

                const result = await response.json();

                // Validar respuesta (nota: es "message" no "mensaje")
                if (result.response && result.response.message === 'OK' && result.data) {
                    const formatted = result.data.map(destino => {
                        // 🔥 El geojson viene como STRING, necesitamos parsearlo
                        let lat = null;
                        let lng = null;

                        try {
                            if (destino.geojson) {
                                const geojsonObj = JSON.parse(destino.geojson);
                                if (geojsonObj.coordinates && geojsonObj.coordinates.length === 2) {
                                    // GeoJSON usa [lng, lat]
                                    lng = geojsonObj.coordinates[0];
                                    lat = geojsonObj.coordinates[1];
                                }
                            }
                        } catch (parseError) {
                            console.warn('Error parseando geojson:', parseError, destino);
                        }

                        // Construir etiqueta descriptiva
                        const label = destino.ent_abr
                            ? `${destino.nombre} (${destino.ent_abr})`
                            : destino.nombre;

                        return {
                            label: label,
                            value: {
                                id_dest: destino.id_dest,
                                name: destino.nombre,
                                address: label,
                                state: destino.ent_abr,
                                lat: lat,
                                lng: lng,
                                raw: destino
                            }
                        };
                    });

                    // Filtrar destinos sin coordenadas válidas
                    const validSuggestions = formatted.filter(s => s.value.lat && s.value.lng);
                    setSuggestions(validSuggestions);
                } else {
                    console.warn('Respuesta inesperada de INEGI:', result);
                    setSuggestions([]);
                }
            } catch (error) {
                console.error('Error buscando en INEGI:', error);
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        }, 400); // ⏳ debounce de 400ms
    };

    const handleSelect = (feature) => {
        // 🔥 Limpiar el input después de seleccionar si clearOnSelect está activo
        if (clearOnSelect) {
            setQuery('');
            setSuggestions([]);
        } else {
            setQuery(feature.value.address);
        }

        if (feature.value.lat && feature.value.lng) {
            onLocationSelect({
                lat: feature.value.lat,
                lng: feature.value.lng,
                name: feature.value.name,
                address: feature.value.address,
                id_dest: feature.value.id_dest,
                state: feature.value.state
            });
        } else {
            console.warn('El destino seleccionado no tiene coordenadas válidas:', feature);
        }
    };

    return (
        <AutoComplete
            placeholder={placeholder}
            value={query}
            suggestions={suggestions}
            completeMethod={searchINEGI}
            field="label"
            onChange={(e) => setQuery(e.value)}
            onSelect={(e) => handleSelect(e.value)}
            loading={loading.toString()}
            style={{ width: '100%' }}
        />
    );
};

export default INEGIGeocoderInput;